import React from 'react';
import { Dialog, DialogContent, Box, Typography, IconButton, Button, Stack } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

/*
  AppDialog: Trello-style dialog shell — the single source of truth for the
  app's dialog look. Built from DialogHeader + DialogFooter so simple forms and
  the larger two-panel dialogs (Rules, Actions) all share the same clean header
  (centered title + close X, no coloured banner), paper radius, and buttons.

  - compact width, centered title with a close X
  - stacked content (use DialogField for label-above-input fields)
  - full-width primary action with a quiet full-width secondary below

  Props:
   - open / onClose: standard dialog wiring (onClose also handles backdrop/escape)
   - title: centered header text
   - primaryLabel / onPrimary / primaryDisabled / primaryColor: main action
     (omit primaryLabel for read-only dialogs — the X is enough)
   - secondaryLabel / onSecondary: quiet action below primary
     (defaults to "Cancel" closing via onSecondary or onClose)
   - maxWidth: MUI width tier, defaults to 'xs' (Trello-compact)
*/

export default function AppDialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  primaryColor = 'primary',
  secondaryLabel = 'Cancel',
  onSecondary,
  maxWidth = 'xs',
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth={maxWidth} fullWidth>
      <DialogHeader title={title} subtitle={subtitle} onClose={onClose} />
      <DialogContent sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
        {children}
        {primaryLabel && (
          <DialogFooter
            layout="stacked"
            primaryLabel={primaryLabel}
            onPrimary={onPrimary}
            primaryDisabled={primaryDisabled}
            primaryColor={primaryColor}
            secondaryLabel={secondaryLabel}
            onSecondary={onSecondary}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/*
  DialogHeader: the shared dialog header — a centered bold title with a quiet
  close X in the top-right. Reused by AppDialog and by the larger dialogs so
  every dialog opens with the same clean, bannerless top.

   - subtitle: optional muted line under the title (e.g. a date or a short
     description) — kept centered to match the title
   - onClose: when omitted, the X is hidden (e.g. a progress dialog mid-run)
   - action: optional control rendered just left of the X (e.g. a mobile
     info toggle on the two-panel dialogs)
*/
export function DialogHeader({ title, subtitle, onClose, action }) {
  return (
    <Box sx={{ position: 'relative', px: 5, pt: 2, pb: subtitle ? 1.5 : 1 }}>
      <Typography variant="subtitle1" fontWeight={700} align="center" noWrap>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" align="center" noWrap sx={{ mt: 0.25 }}>
          {subtitle}
        </Typography>
      )}
      {(action || onClose) && (
        <Box sx={{ position: 'absolute', right: 12, top: 10, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {action}
          {onClose && (
            <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ color: 'text.secondary' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );
}

/*
  DialogSummaryBar: a thin metadata strip rendered just under DialogHeader —
  a left slot and a right slot separated to the edges, with a divider below.
  Used by the day-detail and transaction-list dialogs for the "N items / NET"
  and "N transactions / Total" lines so they read the same way everywhere.
*/
export function DialogSummaryBar({ left, right, sx }) {
  return (
    <Box
      sx={{
        px: 2.5,
        py: 1.25,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        ...sx,
      }}
    >
      {left}
      {right}
    </Box>
  );
}

/*
  DialogFooter: the shared dialog actions.
   - layout="stacked" (default): full-width primary with a quiet full-width
     secondary below — the Trello-compact look used by AppDialog. Rendered
     inline at the foot of the content.
   - layout="inline": a bordered footer bar with the buttons aligned right
     (and optional muted helper text on the left) — used by the wider
     two-panel dialogs where full-width buttons would look stretched.
*/
export function DialogFooter({
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  primaryColor = 'primary',
  secondaryLabel = 'Cancel',
  onSecondary,
  onClose,
  layout = 'stacked',
  info,
}) {
  const stacked = layout === 'stacked';

  const primary = primaryLabel && (
    <Button
      fullWidth={stacked}
      variant="contained"
      color={primaryColor}
      onClick={onPrimary}
      disabled={primaryDisabled}
    >
      {primaryLabel}
    </Button>
  );

  const secondary = secondaryLabel && (
    <Button
      fullWidth={stacked}
      color="inherit"
      onClick={onSecondary || onClose}
      sx={{ color: 'text.secondary' }}
    >
      {secondaryLabel}
    </Button>
  );

  if (stacked) {
    return (
      <Stack spacing={1} sx={{ mt: 3 }}>
        {primary}
        {secondary}
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: info ? 'space-between' : 'flex-end',
        gap: 2,
        px: 3,
        py: 2,
        borderTop: '1px solid',
        borderColor: 'divider',
        flexWrap: 'wrap',
      }}
    >
      {info ? (
        <Typography variant="body2" color="text.secondary">
          {info}
        </Typography>
      ) : null}
      <Stack direction="row" spacing={1.5}>
        {secondary}
        {primary}
      </Stack>
    </Box>
  );
}

/*
  DialogField: Trello-style stacked form field — small bold label above
  the control, optional required marker and helper caption below.
*/
export function DialogField({ label, required, helper, children, sx }) {
  return (
    <Box sx={{ mt: 2, ...sx }}>
      {label && (
        <Typography
          component="label"
          sx={{ display: 'block', fontSize: '0.8125rem', fontWeight: 700, color: 'text.secondary', mb: 0.5 }}
        >
          {label}
          {required && <Box component="span" sx={{ color: 'error.main' }}> *</Box>}
        </Typography>
      )}
      {children}
      {helper && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {helper}
        </Typography>
      )}
    </Box>
  );
}
